﻿import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BookDemoModal } from "./BookDemoModal";

const appUrl = import.meta.env.VITE_APP_URL;

const LINK_CLASS =
  "text-[14.5px] font-medium px-[14px] py-[7px] rounded-lg text-white/[0.72] hover:text-white hover:bg-white/[0.07] transition-all duration-[180ms] whitespace-nowrap cursor-pointer bg-transparent border-0";

export function Navbar() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: "smooth" }); }
    else { window.location.href = `/#${id}`; }
  };

  const headerStyle: React.CSSProperties = scrolled
    ? {
        background: "rgba(11,36,29,0.88)",
        backdropFilter: "blur(16px) saturate(1.3)",
        borderBottom: "1px solid rgba(46,179,154,0.18)",
      }
    : {
        background: "rgba(13,43,34,0)",
        backdropFilter: "blur(0px)",
        borderBottom: "1px solid transparent",
      };

  return (
    <>
      <header
        className="fixed inset-x-0 top-0 z-50 transition-all duration-300"
        style={headerStyle}
      >
        {/* Full-width inner: no max-width cap */}
        <div className="w-full px-6 lg:px-10 py-4">
          <div className="flex items-center justify-between">
            {/* Logo — left */}
            <Link to="/" aria-label="Revisr home" className="flex items-center shrink-0">
              <img src="/revisrlogo3.png" alt="" className="h-auto w-32 flex-none" style={{ objectFit: "contain" }} />
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center">
              <button type="button" onClick={() => scrollTo("problem")} className={LINK_CLASS}>
                For Consultants
              </button>
              <button type="button" onClick={() => scrollTo("how")} className={LINK_CLASS}>
                How It Works
              </button>
              {appUrl && (
                <>
                  <div className="w-px h-4 bg-white/[0.14] mx-1 flex-shrink-0" />
                  <a href={appUrl} className={LINK_CLASS}>
                    Go to app
                  </a>
                </>
              )}
              <button
                type="button"
                onClick={() => setDemoOpen(true)}
                className="ml-1 rounded-xl px-[22px] py-[9px] text-[15px] font-semibold text-[#0D2B22] border border-[#2EB39A] transition-all duration-[180ms] whitespace-nowrap"
                style={{ backgroundColor: "#2EB39A" }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#38C9AE"; e.currentTarget.style.borderColor = "#38C9AE"; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#2EB39A"; e.currentTarget.style.borderColor = "#2EB39A"; }}
              >
                Book a demo
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              type="button"
              className="md:hidden text-white/80 hover:text-white p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile drawer — absolutely positioned so it floats over page content */}
          {mobileOpen && (
            <div
              className="md:hidden absolute left-0 right-0 top-full px-4 pb-4 pt-3 space-y-1"
              style={{
                background: "rgba(11,36,29,0.96)",
                backdropFilter: "blur(16px) saturate(1.3)",
                borderBottom: "1px solid rgba(46,179,154,0.18)",
              }}
            >
              <button type="button" onClick={() => scrollTo("problem")}
                className="block w-full text-left text-[14.5px] font-medium text-white/80 hover:text-white hover:bg-white/[0.07] px-3 py-2.5 rounded-lg transition-all">
                For Consultants
              </button>
              <button type="button" onClick={() => scrollTo("how")}
                className="block w-full text-left text-[14.5px] font-medium text-white/80 hover:text-white hover:bg-white/[0.07] px-3 py-2.5 rounded-lg transition-all">
                How It Works
              </button>
              <div className="flex flex-col gap-2 pt-3">
                {appUrl && (
                  <a href={appUrl}
                    className="block text-[14.5px] font-medium text-white/80 hover:text-white px-3 py-2.5 rounded-lg transition-all">
                    Go to app
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => { setDemoOpen(true); setMobileOpen(false); }}
                  className="w-full rounded-xl py-2.5 text-sm font-semibold text-[#0D2B22] transition-colors"
                  style={{ backgroundColor: "#2EB39A" }}
                >
                  Book a demo
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <BookDemoModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
    </>
  );
}
